import Link from "next/link";
import {
  ApiOutlined,
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  FileTextOutlined,
  FolderOutlined,
  HourglassOutlined,
  LockOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Input, Tag, theme, Tooltip } from "antd";
import { useRouter } from "next/router";
import {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { BuildingIcon } from "./BuildingIcon";
import { FixedHeaderScrollBody } from "./FixedHeaderScrollBody";
import { Flex } from "./Flex";
import { VzblIcon } from "./VzblIcon";
import { FavoriteIcon } from "./FavoriteIcon";
import { Availability } from "pages/api/deep-links/types";
import { AvailabilityStatus } from "./AvailabilityStatus";
import { HoverTag } from "./HoverTag";
import { BookClosedIcon } from "./BookClosedIcon";
import { ItemType } from "./useLibrary";
import { VisibilityIcon, VisibilityType } from "./icons/VisibilityIcon";
import { ItemTypeIcon } from "./icons/ItemTypeIcon";

export function CollectionTagsList({
  filter,
  path,
  collections,
  compact,
  onClick,
}: {
  filter: string;
  path: string;
  collections: string[];
  compact?: boolean;
  onClick?: (filter: string) => void;
}) {
  return collections.length === 0 ? null : (
    <div
      style={{
        padding: compact ? 0 : 16,
        display: compact ? "inline-block" : "block",
      }}
    >
      {!compact && <SmallTitle text="Collections" />}
      <Flex wrap start gap={2}>
        {collections.map((collection) => (
          <FilterTag
            onClick={onClick}
            filters={collectionFilters}
            key={collection}
            filter={filter}
            path={path}
            filterValue={collection}
            filterLabel={collection[0]?.toUpperCase() + collection?.slice(1)}
            icon={
              collection === "favorites" ? (
                <FavoriteIcon
                  size={11}
                  style={{
                    marginRight: 3,
                    position: "relative",
                    top: 2,
                    color: "inherit",
                  }}
                />
              ) : undefined
            }
          />
        ))}
      </Flex>
    </div>
  );
}

export function TypeTagsList({
  filter,
  path,
  compact,
  onClick,
}: {
  filter: string;
  path: string;
  compact?: boolean;
  onClick?: (filter: string) => void;
}) {
  return (
    <div
      style={{
        padding: compact ? 0 : 16,
        display: compact ? "inline-block" : "block",
      }}
    >
      {!compact && <SmallTitle text="Type" />}
      <Flex wrap start>
        <FilterTag
          onClick={onClick}
          filters={typeFilters}
          key={ItemType.BOARDS}
          filter={filter}
          path={path}
          filterValue={ItemType.BOARDS}
          filterLabel={"Board"}
          icon={
            <ItemTypeIcon
              type={ItemType.BOARDS}
              size={11}
              style={{
                marginRight: 3,
                position: "relative",
                top: 2,
                color: "inherit",
              }}
            />
          }
        />
        <FilterTag
          onClick={onClick}
          filters={typeFilters}
          key={ItemType.QUERIES}
          filter={filter}
          path={path}
          filterValue={ItemType.QUERIES}
          filterLabel={"Query"}
          icon={
            <ItemTypeIcon
              type={ItemType.QUERIES}
              size={11}
              style={{
                marginRight: 3,
                position: "relative",
                top: 2,
                color: "inherit",
              }}
            />
          }
        />
        <FilterTag
          onClick={onClick}
          filters={typeFilters}
          key={ItemType.SPACES}
          filter={filter}
          path={path}
          filterValue={ItemType.SPACES}
          filterLabel={"Space"}
          icon={
            <ItemTypeIcon
              type={ItemType.SPACES}
              size={11}
              style={{
                marginRight: 3,
                position: "relative",
                top: 2,
                color: "inherit",
              }}
            />
          }
        />
        <FilterTag
          onClick={onClick}
          filters={typeFilters}
          key={ItemType.TOPICS}
          filter={filter}
          path={path}
          filterValue={ItemType.TOPICS}
          filterLabel={"Topic"}
          icon={
            <ItemTypeIcon
              type={ItemType.TOPICS}
              size={11}
              style={{
                marginRight: 3,
                position: "relative",
                top: 2,
                color: "inherit",
              }}
            />
          }
        />
        <FilterTag
          onClick={onClick}
          filters={typeFilters}
          key={ItemType.REPORTS}
          filter={filter}
          path={path}
          filterValue={ItemType.REPORTS}
          filterLabel={"Report"}
          icon={
            <ItemTypeIcon
              type={ItemType.REPORTS}
              size={11}
              style={{
                marginRight: 3,
                position: "relative",
                top: 2,
                color: "inherit",
              }}
            />
          }
        />
      </Flex>
    </div>
  );
}

export function VisibilityTagsList({
  compact,
  filter,
  onClick,
  path,
}: {
  compact?: boolean;
  filter: string;
  path: string;
  onClick?: (filter: string) => void;
}) {
  const router = useRouter();
  const { token } = theme.useToken();

  return (
    <div
      style={{
        padding: compact ? 0 : 16,
        display: compact ? "inline-block" : "block",
      }}
    >
      {!compact && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: token.colorTextTertiary,
            textTransform: "uppercase",
          }}
        >
          Visibility
        </div>
      )}
      <Flex
        start
        wrap
        style={{ fontSize: 12, alignItems: "flex-end", paddingTop: 2 }}
      >
        <HoverTag
          style={{ marginBottom: 4 }}
          color={
            scopeFilters.has(filter, "private")
              ? token.colorPrimaryActive
              : undefined
          }
        >
          <Link
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                filter: scopeFilters.toggle(filter, "private"),
              },
            }}
            onClick={(e) => {
              if (onClick) {
                e.preventDefault();
                onClick(scopeFilters.toggle(filter, "private"));
              }
            }}
          >
            <Tooltip title="Private">
              <span style={{ display: "flex", alignItems: "center" }}>
                <VisibilityIcon
                  visibility={VisibilityType.PRIVATE}
                  size={12}
                  style={{ fontSize: 10, marginRight: 4 }}
                />
                {!compact && " Private"}
              </span>
            </Tooltip>
          </Link>
        </HoverTag>
        <HoverTag
          style={{ marginBottom: 4 }}
          color={
            scopeFilters.has(filter, "org")
              ? token.colorPrimaryActive
              : undefined
          }
        >
          <Link
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                filter: scopeFilters.toggle(filter, "org"),
              },
            }}
            onClick={(e) => {
              if (onClick) {
                e.preventDefault();
                onClick(scopeFilters.toggle(filter, "org"));
              }
            }}
          >
            <Tooltip title="Organization">
              <VisibilityIcon
                visibility={VisibilityType.ORG}
                size={12}
                style={{ display: "block", fontSize: 10 }}
              />
              {!compact && " Org"}
            </Tooltip>
          </Link>
        </HoverTag>
        <HoverTag
          style={{ marginBottom: 4 }}
          color={
            scopeFilters.has(filter, "global")
              ? token.colorPrimaryActive
              : undefined
          }
        >
          <Link
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                filter: scopeFilters.toggle(filter, "global"),
              },
            }}
            onClick={(e) => {
              if (onClick) {
                e.preventDefault();
                onClick(scopeFilters.toggle(filter, "global"));
              }
            }}
          >
            <Tooltip title="Global">
              <VisibilityIcon
                visibility={VisibilityType.GLOBAL}
                size={12}
                style={{ display: "block", fontSize: 10, fill: "currentColor" }}
              />
              {!compact && " Global"}
            </Tooltip>
          </Link>
        </HoverTag>
      </Flex>
    </div>
  );
}

export function ListPageSider({ children }: PropsWithChildren) {
  return (
    <FixedHeaderScrollBody style={{ flex: 3 }}>
      {children}
    </FixedHeaderScrollBody>
  );
}

export function ListPageBody({ children }: PropsWithChildren) {
  return (
    <Flex start gap={16} direction="column" style={{ width: "100%", flex: 9 }}>
      {children}
    </Flex>
  );
}

export function ListPageResults({ children }: PropsWithChildren) {
  return (
    <div
      style={{
        flex: 1,
        overflow: "hidden",
        width: "100%",
      }}
    >
      {children}
    </div>
  );
}

export function RelatedTagsList({
  filter,
  tags,
  path,
  title = "Related Tags",
  style,
}: {
  filter: string;
  tags: string[];
  path: string;
  title?: string;
  style?: CSSProperties;
}) {
  return tags.length === 0 ? null : (
    <div style={{ padding: 16, ...style }}>
      <SmallTitle text={title} />
      <Flex wrap start>
        {tags.map((tag) => (
          <FilterTag
            key={tag}
            filter={filter}
            path={path}
            filterValue={tag}
            filters={tagFilters}
          />
        ))}
      </Flex>
    </div>
  );
}

export function StatusTagsList({
  compact,
  filter,
  onClick,
  path,
}: {
  compact?: boolean;
  filter: string;
  path: string;
  onClick?: (filter: string) => void;
}) {
  const router = useRouter();
  const { token } = theme.useToken();

  return (
    <div
      style={{
        padding: compact ? 0 : 16,
        display: compact ? "inline-block" : "block",
      }}
    >
      {!compact && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: token.colorTextTertiary,
            textTransform: "uppercase",
          }}
        >
          Status
        </div>
      )}
      <Flex
        start
        wrap
        style={{ fontSize: 12, alignItems: "flex-end", paddingTop: 2 }}
      >
        <HoverTag
          style={{ marginBottom: 4 }}
          color={
            statusFilters.has(filter, "populated")
              ? token.colorPrimaryActive
              : undefined
          }
        >
          <Link
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                filter: statusFilters.toggle(filter, "populated"),
              },
            }}
            onClick={(e) => {
              if (onClick) {
                e.preventDefault();
                onClick(statusFilters.toggle(filter, "populated"));
              }
            }}
          >
            <CheckOutlined />
            <div>Populated</div>
          </Link>
        </HoverTag>
        <HoverTag
          style={{ marginBottom: 4 }}
          color={
            statusFilters.has(filter, "processing")
              ? token.colorPrimaryActive
              : undefined
          }
        >
          <Link
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                filter: statusFilters.toggle(filter, "processing"),
              },
            }}
            onClick={(e) => {
              if (onClick) {
                e.preventDefault();
                onClick(statusFilters.toggle(filter, "processing"));
              }
            }}
          >
            <HourglassOutlined
              size={12}
              style={{ display: "block", fontSize: 10 }}
            />
            <div>Processing</div>
          </Link>
        </HoverTag>
        <HoverTag
          style={{ marginBottom: 4 }}
          color={
            statusFilters.has(filter, "notConfigured")
              ? token.colorPrimaryActive
              : undefined
          }
        >
          <Link
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                filter: statusFilters.toggle(filter, "notConfigured"),
              },
            }}
            onClick={(e) => {
              if (onClick) {
                e.preventDefault();
                onClick(statusFilters.toggle(filter, "notConfigured"));
              }
            }}
          >
            <ApiOutlined style={{ fill: "currentColor" }} />
            <div>Lacks integration</div>
          </Link>
        </HoverTag>
      </Flex>
    </div>
  );
}

export function BigTitle({
  text,
  style,
}: {
  text: ReactNode | string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        padding: 16,
        fontSize: 36,
        fontWeight: 400,
        marginBottom: 6,
        ...style,
      }}
    >
      {text}
    </div>
  );
}

export function MediumTitle({
  text,
  style,
}: {
  text: ReactNode | string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        padding: 12,
        fontSize: 24,
        fontWeight: 500,
        marginBottom: 6,
        ...style,
      }}
    >
      {text}
    </div>
  );
}

export function SmallTitle({ text, color }: { text: string; color?: string }) {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: color || token.colorTextTertiary,
        textTransform: "uppercase",
      }}
    >
      {text}
    </div>
  );
}

export function FilterTag({
  filter,
  filterValue,
  filterLabel,
  path,
  icon,
  filters,
  onClick,
  style,
}: {
  filter: string;
  filterValue: string;
  filterLabel?: string;
  path: string;
  icon?: ReactNode;
  filters: ReturnType<typeof makeFilterToggle>;
  onClick?: (filter: string) => void;
  style?: CSSProperties;
}) {
  const router = useRouter();
  const { token } = theme.useToken();
  return (
    <HoverTag
      key={filterValue}
      className={
        filters.has(filter, filterValue) ? "vzbl-pill-selectable" : undefined
      }
      style={{ marginBottom: 4, ...style }}
    >
      <Link
        href={{
          pathname: `/[orgId]/${path}`,
          query: {
            ...router.query,
            filter: filters.toggle(filter, filterValue),
          },
        }}
        key={filterValue}
        onClick={(e) => {
          if (onClick) {
            e.preventDefault();
            onClick(filters.toggle(filter, filterValue));
          }
        }}
      >
        {icon}
        {filterLabel ? filterLabel : filterValue}
      </Link>
    </HoverTag>
  );
}

export enum ListPageSort {
  "alpha_asc" = "Sort A-Z",
  "alpha_dsc" = "Sort Z-A",
  "type_asc" = "Type (asc)",
  "type_dsc" = "Type (dsc)",
  "scope_asc" = "Visibility (asc)",
  "scope_dsc" = "Visibility (dsc)",
  "status_asc" = "Status (asc)",
  "status_dsc" = "Status (dsc)",
}

export interface ListHeaderColumns {
  name?: boolean;
  type?: boolean;
  visibility?: boolean;
  status?: boolean;
  favorites?: boolean;
}

export function ListHeaderRow({
  sort,
  path,
  columns = {
    name: true,
    type: true,
    visibility: true,
    status: true,
    favorites: true,
  },
}: {
  sort: ListPageSort;
  path: string;
  columns?: ListHeaderColumns;
}) {
  const router = useRouter();
  const { token } = theme.useToken();
  return (
    <Flex
      start
      style={{
        width: "calc(100% - 16px)",
        backgroundColor: token.colorBgLayout,
        paddingTop: 16,
        paddingBottom: 16,
        borderRadius: 3,
        fontSize: 12,
        fontWeight: 400,
        color: token.colorTextTertiary,
        marginBottom: 2,
      }}
    >
      {columns.name && (
        <div style={{ flex: 7, minWidth: 0 }}>
          <Link
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                sort:
                  sort === ListPageSort.alpha_asc
                    ? ListPageSort.alpha_dsc
                    : ListPageSort.alpha_asc,
              },
            }}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <span
              style={{
                paddingLeft: 16,
                paddingRight: 8,
                fontWeight: 600,
                fontSize: 10,
              }}
            >
              NAME
            </span>
            {sort === ListPageSort.alpha_asc ? (
              <ArrowUpOutlined />
            ) : sort === ListPageSort.alpha_dsc ? (
              <ArrowDownOutlined />
            ) : undefined}
          </Link>
        </div>
      )}
      {columns.favorites && (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: "center",
          }}
        >
          <Link
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                sort:
                  sort === ListPageSort.scope_asc
                    ? ListPageSort.scope_dsc
                    : ListPageSort.scope_asc,
              },
            }}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <span
              style={{
                paddingLeft: 16,
                paddingRight: 8,
                fontWeight: 600,
                fontSize: 10,
              }}
            >
              FAVORITE
            </span>
            {sort === ListPageSort.scope_asc ? (
              <ArrowUpOutlined />
            ) : sort === ListPageSort.scope_dsc ? (
              <ArrowDownOutlined />
            ) : undefined}
          </Link>
        </div>
      )}
      {columns.type && (
        <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
          <Link
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                sort:
                  sort === ListPageSort.type_asc
                    ? ListPageSort.type_dsc
                    : ListPageSort.type_asc,
              },
            }}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <span
              style={{
                paddingLeft: 16,
                paddingRight: 8,
                fontWeight: 600,
                fontSize: 10,
              }}
            >
              TYPE
            </span>
            {sort === ListPageSort.type_asc ? (
              <ArrowUpOutlined />
            ) : sort === ListPageSort.type_dsc ? (
              <ArrowDownOutlined />
            ) : undefined}
          </Link>
        </div>
      )}
      {columns.visibility && (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: "center",
          }}
        >
          <Link
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                sort:
                  sort === ListPageSort.scope_asc
                    ? ListPageSort.scope_dsc
                    : ListPageSort.scope_asc,
              },
            }}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <span
              style={{
                paddingLeft: 16,
                paddingRight: 8,
                fontWeight: 600,
                fontSize: 10,
              }}
            >
              VISIBILITY
            </span>
            {sort === ListPageSort.scope_asc ? (
              <ArrowUpOutlined />
            ) : sort === ListPageSort.scope_dsc ? (
              <ArrowDownOutlined />
            ) : undefined}
          </Link>
        </div>
      )}
      {columns.status && (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: "center",
          }}
        >
          <Link
            href={{
              pathname: `/[orgId]/${path}`,
              query: {
                ...router.query,
                sort:
                  sort === ListPageSort.status_asc
                    ? ListPageSort.status_dsc
                    : ListPageSort.status_asc,
              },
            }}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <span
              style={{
                paddingLeft: 16,
                paddingRight: 8,
                fontWeight: 600,
                fontSize: 10,
              }}
            >
              STATUS
            </span>
            {sort === ListPageSort.status_asc ? (
              <ArrowUpOutlined />
            ) : sort === ListPageSort.status_dsc ? (
              <ArrowDownOutlined />
            ) : undefined}
          </Link>
        </div>
      )}
    </Flex>
  );
}

export const LIST_ITEM_ROW_HEIGHT = 100;
export function ListItemRow(props: {
  title: string;
  description?: string;
  tags: string[];
  filter: string;
  icon: ReactNode;
  scope?: VisibilityType;
  clickHandler: "link" | "onclick";
  pathname?: string;
  query?: {};
  path?: string;
  availability?: Availability;
  onClick?: () => void;
  columns?: ListHeaderColumns;
  favoriteIcon?: ReactNode;
}) {
  const { token } = theme.useToken();
  const columns = props.columns || {
    favorites: true,
    name: true,
    type: true,
    visibility: true,
    status: true,
  };

  const content = (
    <Flex start style={{ alignItems: "center", height: "100%" }}>
      {columns.name && (
        <div
          style={{
            flex: 7,
            minWidth: 0,
            color: token.colorTextTertiary,
            fontSize: 14,
            lineHeight: "20px",
          }}
        >
          <Flex spaceBetween gap={16}>
            <div
              style={{
                minWidth: 0,
                paddingLeft: 16,
                maxWidth: "calc(80% - 16px)",
              }}
            >
              <strong
                style={{
                  fontWeight: 600,
                  color: token.colorText,
                  flex: 1,
                }}
                className="trunc"
              >
                {props.title}
              </strong>
              <div className="line-clamp-2">{props.description || ""}</div>
            </div>
            <div style={{ minWidth: 0, maxWidth: "50%", textAlign: "right" }}>
              <div className="line-clamp-2" style={{ textAlign: "right" }}>
                {props.tags.map((tag) => (
                  <FilterTag
                    filters={tagFilters}
                    key={tag}
                    filterValue={tag}
                    filter={props.filter}
                    path={props.path || ""}
                    style={{ marginBottom: 4 }}
                  />
                ))}
              </div>
            </div>
          </Flex>
        </div>
      )}
      {columns.favorites && (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: "center",
            color: token.colorText,
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {props.favoriteIcon}
        </div>
      )}
      {columns.type && (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 16,
              color: token.colorTextSecondary,
            }}
          >
            {props.icon}
          </div>
        </div>
      )}
      {columns.visibility && (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            color: token.colorTextSecondary,
            fontSize: 13,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <VisibilityIcon
            visibility={props.scope || VisibilityType.GLOBAL}
            size={14}
          />
        </div>
      )}
      {columns.status && (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: "center",
          }}
        >
          <AvailabilityStatus availability={props.availability} />
        </div>
      )}
    </Flex>
  );

  const sharedStyles = {
    display: "block",
    textDecoration: "none",
    color: "inherit",
    paddingTop: 16,
    paddingBottom: 16,
    height: LIST_ITEM_ROW_HEIGHT - 2,
    overflow: "hidden",
  };

  return (
    <div
      style={{
        borderBottom: `solid 1px ${token.colorBorderSecondary}`,
        marginRight: 16,
      }}
      className="hover-show item-link"
    >
      {props.clickHandler === "onclick" ? (
        <div
          onClick={props.onClick}
          style={{ ...sharedStyles, cursor: "pointer" }}
          role="button"
          tabIndex={0}
        >
          {content}
        </div>
      ) : (
        <Link
          href={{ pathname: props.pathname, query: props.query }}
          style={sharedStyles}
        >
          {content}
        </Link>
      )}
    </div>
  );
}

export function ListHeaderControls({
  children,
  placeholder,
  filter,
  setFilter,
}: PropsWithChildren<{
  placeholder: string;
  setFilter(filter: string): void;
  filter: string;
}>) {
  const [localFilter, setLocalFilter] = useState(filter);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilter(localFilter);
    }, 500); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [localFilter, setFilter]);

  useEffect(() => {
    setLocalFilter(filter);
  }, [filter]);

  return (
    <Flex
      gap={16}
      style={{
        width: "100%",
        paddingTop: 16,
        paddingRight: 16,
      }}
    >
      <Input
        placeholder={placeholder}
        onChange={(e) => setLocalFilter(e.target.value)}
        allowClear
        value={localFilter}
        addonBefore={<SearchOutlined />}
      />
      {children}
    </Flex>
  );
}

export function ListPageWrapper({ children }: PropsWithChildren) {
  const { token } = theme.useToken();
  return <div className="vzbl-panel-standard">{children}</div>;
}

export const tagFilters = makeFilterToggle(
  (tag: string) => `tag:${tag.replace(/\s/gm, "_")}`,
  (tagFilter: string) => tagFilter.slice(4).replaceAll("_", " ")
);

export const scopeFilters = makeFilterToggle(
  (scope: string) => `scope:${scope}`,
  (scopeFilter: string) => scopeFilter.slice(6)
);

export const collectionFilters = makeFilterToggle(
  (collection: string) => `in:${collection}`,
  (collectionFilter: string) => collectionFilter.slice(3)
);

export const typeFilters = makeFilterToggle(
  (collection: string) => `type:${collection}`,
  (collectionFilter: string) => collectionFilter.slice(5)
);

export const statusFilters = makeFilterToggle(
  (status: string) => `status:${status}`,
  (statusFilter: string) => statusFilter.slice(7)
);

export function makeFilterToggle(
  toFilter: (text: string) => string,
  fromFilter: (text: string) => string
) {
  function has(filter: string, tag: string): boolean {
    return filter.split(" ").includes(toFilter(tag));
  }

  function add(filter: string, tag: string): string {
    return `${filter}${filter ? " " : ""}${toFilter(tag)}`;
  }

  function remove(filter: string, tag: string): string {
    return filter
      .split(" ")
      .filter((term) => term !== toFilter(tag))
      .join(" ");
  }

  function toggle(filter: string, tag: string): string {
    return has(filter, tag) ? remove(filter, tag) : add(filter, tag);
  }
  return { toggle, has, add, remove, fromFilter, toFilter };
}
